/**
 * Outil « Carré dont on règle l'aire » — calculs (pur, entiers exacts).
 *
 * Un carré d'aire A (entier) a pour côté √A. On l'encadre entre deux entiers
 * consécutifs par les carrés parfaits (n² ≤ A < (n + 1)²), puis au dixième
 * et au centième : k = ⌊√(A × 10²ᵈ)⌋ calculé en ENTIERS (jamais un arrondi
 * flottant à la frontière), k² et (k + 1)² écrits exactement. En 2nde,
 * √A = k√b avec k² le plus grand carré qui divise A.
 */

export const MAX_AREA = 150;

/** ⌊√n⌋ exact pour un entier n ≥ 0 */
export function isqrt(n: number): number {
  if (n < 0) throw new Error('négatif');
  let r = Math.floor(Math.sqrt(n));
  while (r * r > n) r--;
  while ((r + 1) * (r + 1) <= n) r++;
  return r;
}

export function isPerfectSquare(n: number): boolean {
  const r = isqrt(n);
  return r * r === n;
}

/** un entier divisé par 10^d, écrit à la française : (1156, 2) → « 11,56 » */
export function scaled(k: number, d: number): string {
  if (d === 0) return String(k);
  const s = String(k).padStart(d + 1, '0');
  return `${s.slice(0, -d)},${s.slice(-d)}`;
}

export interface Bracket {
  /** nombre de décimales de l'encadrement (0, 1, 2) */
  digits: number;
  /** bornes ×10^digits : lo = k, hi = k + 1 */
  k: number;
  lo: string;
  hi: string;
  /** leurs carrés, exacts : « 11,56 », « 12,25 » */
  loSq: string;
  hiSq: string;
  /** la borne basse est-elle exactement √A ? (carré parfait au dixième, au centième…) */
  exact: boolean;
}

/** Encadrement de √A à 10^−digits près, en entiers. */
export function bracket(area: number, digits: number): Bracket {
  const scale = 10 ** (2 * digits);
  const k = isqrt(area * scale);
  return {
    digits,
    k,
    lo: scaled(k, digits),
    hi: scaled(k + 1, digits),
    loSq: scaled(k * k, 2 * digits),
    hiSq: scaled((k + 1) * (k + 1), 2 * digits),
    exact: k * k === area * scale,
  };
}

/** √A = k√b avec k le plus grand possible (b sans facteur carré). */
export function simplify(area: number): { k: number; b: number } {
  for (let k = isqrt(area); k >= 1; k--) if (area % (k * k) === 0) return { k, b: area / (k * k) };
  return { k: 1, b: area };
}

/**
 * Début de l'écriture décimale de √A, TRONQUÉE (jamais arrondie) : « 3,464 »
 * pour √12 = 3,4641…, « 9,949 » pour √99 = 9,9498… — cohérent avec les
 * encadrements affichés au-dessus.
 */
export function truncated(area: number, digits = 3): string {
  return bracket(area, digits).lo;
}
